import { isoWeekdayOf } from '@app/hr/hr-schedule-engine';
import type { Day, DayState, RentalLineView } from './rental-billing.model';
import type { RangeEditTarget } from './rental-dialogs.contract';
import { addDays, addMonthsClamped, daysBetween, daysInMonth, frWeekday, isDay, maxDay, minDay } from './rental-days';
import { deliveredDay } from './rental-planning.geometry';

// Calendrier de deux mois : géométrie de la sélection et marques lues dans les états et décisions rendus par le serveur.
// Une plage choisie va du start au end, les DEUX inclus (règle du 27/10/2026) : le 2e clic désigne le dernier jour, jamais un lendemain.
export interface PickerMonth { first: Day; blanks: number; days: Day[]; } // blanks = isoWeekdayOf(first) − 1 (lundi en tête)

export function pickerMonthOf(first: Day): PickerMonth {
  const n = daysInMonth(Number(first.slice(0, 4)), Number(first.slice(5, 7)));
  const days: Day[] = [];
  for (let i = 0; i < n; i++) days.push(addDays(first, i));
  return { first, blanks: isoWeekdayOf(first) - 1, days };
}

// end : dernier jour choisi, inclus ; startEff : début affiché (start, ou le plancher « dès la livraison »).
export interface PickerSel { start: Day | null; startEff: Day | null; end: Day | null; hover: Day | null; phase: 'start' | 'end'; }
// st = début, en = dernier jour, inr = entre les deux, open = plage sans fin (interruption ouverte), solo = début seul affiché.
export interface PickerDayFlags { st: boolean; solo: boolean; inr: boolean; open: boolean; en: boolean; }

export function pickerDayFlags(d: Day, s: PickerSel): PickerDayFlags {
  const flags: PickerDayFlags = { st: false, solo: false, inr: false, open: false, en: false };
  const se = s.startEff;
  if (se === null) return flags;
  let rangeEnd: Day | null = s.end;
  if (rangeEnd === null && s.phase === 'end' && s.hover !== null && s.hover >= se) rangeEnd = s.hover;
  const openEnded = s.end === null && s.phase === 'start';
  if (d === se) {
    flags.st = true;
    flags.solo = (rangeEnd === null && !openEnded) || rangeEnd === se;
  } else if (rangeEnd !== null && d > se && d < rangeEnd) {
    flags.inr = true;
  } else if (openEnded && d > se) {
    flags.inr = true;
    flags.open = true;
  }
  if (rangeEnd !== null && d === rangeEnd) flags.en = true;
  return flags;
}

// Un clic avant le début (ou en phase début) pose un nouveau début ; sinon il pose le dernier jour, inclus (le début lui-même = plage d'un jour).
export function pickerClick(d: Day, s: PickerSel): { sel: PickerSel } {
  const se = s.startEff;
  if (s.phase === 'start' || se === null || d < se) return { sel: { ...s, start: d, startEff: d, end: null, hover: null, phase: 'end' } };
  return { sel: { ...s, end: d, hover: null, phase: 'start' } };
}

export function pickerKey(focus: Day, key: string): Day | null {
  switch (key) {
    case 'ArrowLeft': return addDays(focus, -1);
    case 'ArrowRight': return addDays(focus, 1);
    case 'ArrowUp': return addDays(focus, -7);
    case 'ArrowDown': return addDays(focus, 7);
    case 'PageUp': return addMonthsClamped(focus, -1);
    case 'PageDown': return addMonthsClamped(focus, 1);
    default: return null;
  }
}

// Seul analyseur des dates tapées des dialogues : « 28 » → jour du mois affiché (defaultMonth, AAAA-MM), « 11/6 », « 11 6 », « 11/6/26 », « 11/06/2026 », « 2026-06-11 » ;
// '' → null ; incompris → undefined.
export function parseTypedDay(text: string, defaultMonth: string): Day | null | undefined {
  const t = (text ?? '').trim();
  if (!t) return null;
  if (isDay(t)) return t;
  const m = /^(\d{1,2})(?:[/.\-\s]+(\d{1,2})(?:[/.\-\s]+(\d{2}|\d{4}))?)?$/.exec(t);
  if (!m) return undefined;
  const year = m[3] ? (m[3].length === 2 ? 2000 + Number(m[3]) : Number(m[3])) : Number(defaultMonth.slice(0, 4));
  const month = m[2] ? Number(m[2]) : Number(defaultMonth.slice(5, 7));
  const day = Number(m[1]);
  const candidate = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return isDay(candidate) ? candidate : undefined;
}

export interface PickerMarks {
  locked: ReadonlySet<Day>; dotted: ReadonlySet<Day>; disabledBefore: Day | null; disabledFrom: Day | null; tip(d: Day): string;
  // Couverture par ordonnance (dialogue Prescriptions) : présente, elle remplace le trait des jours facturés et l'anneau d'aujourd'hui.
  covered?: ReadonlySet<Day>; partly?: ReadonlySet<Day>; missing?: Day | null;
}

const LOCKED_STATES: ReadonlySet<DayState> = new Set<DayState>(['BILLED', 'BILLED_EARLY', 'PAID_INTERRUPTED', 'COMPENSATED']);

// Dernier jour marqué d'une interruption ouverte (inclus) : dernier jour de la frise de la ligne, à défaut un an.
function frontierOf(l: RentalLineView, start: Day): Day {
  return l.days?.length ? (maxDay(l.days[l.days.length - 1].end, start) as Day) : addDays(start, 365);
}

// locked = jours déjà facturés ; dotted = jours déjà interrompus (gestes compris) ; disabledBefore / disabledFrom : jours non cliquables.
export function pickerMarksOf(lines: RentalLineView[], opts: { except?: RangeEditTarget[]; kind: 'interruption' | 'start' | 'end' | 'prescription' }): PickerMarks {
  const locked = new Set<Day>();
  const dotted = new Set<Day>();
  const except = new Set((opts.except ?? []).map((e) => e.lineId + ':' + e.id));
  for (const l of lines) {
    for (const r of l.days ?? []) {
      if (!LOCKED_STATES.has(r.state)) continue;
      for (let d = r.start; d <= r.end; d = addDays(d, 1)) locked.add(d);
    }
    for (const i of l.decisions?.interruptions ?? []) {
      if (i.cancelled || except.has(l.id + ':' + i.id)) continue;
      const start = i.start ?? l.summary?.window_start ?? deliveredDay(l);
      if (!start) continue;
      const end = i.end ?? frontierOf(l, start);
      for (let d = start; d <= end; d = addDays(d, 1)) dotted.add(d);
    }
  }
  const delivered = lines.map(deliveredDay);
  const allDelivered = lines.length > 0 && delivered.every((d) => !!d);
  const firstReturned = lines.map((l) => l.days?.find((r) => r.state === 'RETURNED')?.start ?? null);
  let disabledBefore: Day | null = null;
  let disabledFrom: Day | null = null;
  if (opts.kind === 'start') {
    disabledFrom = minDay(...delivered);
  } else if (opts.kind === 'interruption') {
    if (allDelivered) disabledBefore = minDay(...delivered);
    if (lines.length > 0 && firstReturned.every((d) => !!d)) disabledFrom = maxDay(...firstReturned);
  }
  const kind = opts.kind;
  return {
    locked, dotted, disabledBefore, disabledFrom,
    tip(d: Day): string {
      const t: string[] = [];
      if (locked.has(d)) t.push('déjà facturé');
      if (dotted.has(d)) t.push('déjà interrompu');
      if (disabledFrom !== null && d >= disabledFrom) t.push(kind === 'start' ? 'à partir de la livraison' : 'matériel repris');
      if (disabledBefore !== null && d < disabledBefore) t.push(kind === 'interruption' ? 'avant la livraison : utiliser « Dès la livraison »' : 'avant la livraison');
      return frWeekday(d) + (t.length ? ' (' + t.join(', ') + ')' : '');
    },
  };
}

// Jours verrouillés du start au end, les deux inclus ; end null : jusqu'au dernier verrou.
export function lockedDaysIn(m: PickerMarks, start: Day, end: Day | null): number {
  let n = 0;
  for (const d of m.locked) if (d >= start && (end === null || d <= end)) n++;
  return n;
}

// Interruption (geste compris) du start au end, les deux inclus, comme RangeDialogData ; start null = dès la livraison (floor), end null = ouverte.
// Seuls les jours saisis sont montrés, le dernier inclus : jamais de lendemain (règle du 27/10/2026).
export function rangeText(start: Day | null, end: Day | null, floor: Day | null): { days: number | null; text: string } {
  const plural = (n: number, w: string) => `${n} ${w}${n > 1 ? 's' : ''}`;
  const eff = start ?? floor;
  if (end === null) {
    if (start) return { days: null, text: `Interruption à partir du ${frWeekday(start)}, jusqu'à nouvel ordre (interruption ouverte)` };
    return { days: null, text: floor ? `Interruption dès la livraison du ${frWeekday(floor)}, jusqu'à nouvel ordre (interruption ouverte)` : `Interruption dès la livraison, jusqu'à nouvel ordre (interruption ouverte)` };
  }
  if (!eff) return { days: null, text: `Interruption dès la livraison, jusqu'au ${frWeekday(end)} inclus` };
  if (end < eff) return { days: null, text: '' };
  const n = daysBetween(eff, end) + 1;
  const from = start ? `du ${frWeekday(eff)}` : `dès la livraison du ${frWeekday(eff)}`;
  return { days: n, text: `${plural(n, 'jour')} d'interruption (${from} au ${frWeekday(end)} inclus)` };
}
